import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  HospitalContactForm,
  hospitalContactFormSchema,
  DEFAULT_HOSPITAL_CONTACT_VALUES,
} from '@lp/features/admin/forms/HospitalContactForm';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCreateHospitalContactMutation } from '@/queries/hospital-contacts';
import { ContactFormValues } from '@/types';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

export const HospitalContactCreate = () => {
  const navigate = useNavigate();
  const { mutateAsync } = useCreateHospitalContactMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.HospitalCreate);

  const handleBackToList = () => {
    navigate('/admin/hospitals/contacts');
  };

  const handleSubmit = async (data: ContactFormValues) => {
    try {
      let uploadedFile;

      if (data.photo) {
        uploadedFile = await uploadFiles([data.photo]);
      }

      await mutateAsync({
        parentId: data.parentId,
        firstName: data.firstName,
        lastName: data.lastName,
        primaryPhone: data.primaryPhone,
        photoPath: uploadedFile ? uploadedFile[0].fileName : undefined,
        ...(data.email ? { email: data.email } : {}),
        ...(data.secondaryPhone ? { secondaryPhone: data.secondaryPhone } : {}),
      });

      toast.success('Contact created successfully.');

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Form<ContactFormValues>
      defaultValues={DEFAULT_HOSPITAL_CONTACT_VALUES}
      schema={hospitalContactFormSchema}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-9">
        <AdminPageHeader title="Create Donor Hospital Contact" backToListLink="/admin/hospitals/contacts">
          <AdminFormAction type="create" handleClick={handleBackToList} hasCreatePermissions={hasCreatePermissions} />
        </AdminPageHeader>
        <HospitalContactForm isReadOnly={!hasCreatePermissions} />
      </div>
    </Form>
  );
};
